import React, { useEffect, useState } from 'react';
import styles from './AdminShared.module.scss';
import { EventTypeCreateDto, EventTypeDto } from '../../../types/admin.types';
import { createAdminEventType, deleteAdminEventType, fetchAdminEventTypes, updateAdminEventType } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const emptyForm: EventTypeCreateDto = {
    name: ''
};

const AdminEventTypes = () => {
    const { getUserInfo } = useUserState();
    const [items, setItems] = useState<EventTypeDto[]>([]);
    const [form, setForm] = useState<EventTypeCreateDto>(emptyForm);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const token = getUserInfo()?.token;

    const loadItems = async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const response = await fetchAdminEventTypes(token);
            setItems(response);
        } catch (error) {
            setErrorMessage('Unable to load video types.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedId(null);
    };

    const handleEdit = (item: EventTypeDto) => {
        setSelectedId(item.id);
        setForm({ name: item.name });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage(null);
        try {
            if (selectedId) {
                await updateAdminEventType(selectedId, form, token);
            } else {
                await createAdminEventType(form, token);
            }
            await loadItems();
            resetForm();
        } catch (error) {
            setErrorMessage('Unable to save video type.');
        }
    };

    const handleDelete = async (id: string) => {
        setErrorMessage(null);
        try {
            await deleteAdminEventType(id, token);
            if (selectedId === id) {
                resetForm();
            }
            await loadItems();
        } catch (error) {
            setErrorMessage('Unable to delete video type.');
        }
    };

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Video Types</h1>
                    <p>Manage video categories like MMA, boxing, or interviews.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Current Video Types</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading video types...</div>
                    ) : items.length === 0 ? (
                        <div className={styles.empty}>No video types yet.</div>
                    ) : (
                        items.map((item) => (
                            <div key={item.id} className={styles.listItem}>
                                <div>
                                    <strong>{item.name}</strong>
                                </div>
                                <div className={styles.rowActions}>
                                    <button type="button" className={styles.secondary} onClick={() => handleEdit(item)}>Edit</button>
                                    <button type="button" className={styles.danger} onClick={() => handleDelete(item.id)}>Delete</button>
                                </div>
                            </div>
                        ))
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>{selectedId ? 'Edit Video Type' : 'New Video Type'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.field}>
                            <label htmlFor="event-type-name">Name</label>
                            <input
                                id="event-type-name"
                                value={form.name}
                                onChange={(event) => setForm({ name: event.target.value })}
                                maxLength={200}
                                required
                            />
                        </div>
                        <div className={styles.actions}>
                            <button className={styles.primary} type="submit">
                                {selectedId ? 'Update Video Type' : 'Create Video Type'}
                            </button>
                            {selectedId ? (
                                <button type="button" className={styles.secondary} onClick={resetForm}>Cancel</button>
                            ) : null}
                        </div>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default AdminEventTypes;
